import React from 'react';

import { isDefined, isEmpty } from '@nfctron/nexus-core';
import { cn } from '@nfctron/nexus-react';

import { Radio as MantineRadio } from '@mantine/core';

import { type Component, type ComponentDisplayName } from '@root/lib/types/components';

import { getTextFieldErrorComponent } from '../TextInput/TextInput';

import { type RadioGroupProps } from './RadioGroup.types';

/**
 * RadioGroup component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const RadioGroup: Component<RadioGroupProps> = ({ value: inputValue, disabled, data, error, children, renderOption, ...rest }) => (
	<MantineRadio.Group
		error={getTextFieldErrorComponent(error)}
		/** @ts-ignore */
		value={inputValue ?? null}
		classNames={{ root: 'flex flex-col gap-2' }}
		{...rest}
	>
		{data.map((item, index) => {
			const isActive = item.value === inputValue;
			const isDisabled = disabled ?? item.disabled;
			const { icon, value, description, ...radioProps } = item;
			const radioNode = (
				<MantineRadio
					key={item.value}
					color="purple-heart"
					value={value}
					{...radioProps}
					label={
						<div className="flex w-full items-center justify-between gap-2">
							{/* label */}
							<div className="py-2">
								<span>{item.label}</span>
								{!isEmpty(description) && (
									<span className={cn(['text-md mt-0.5 block font-normal text-gray-500', isActive && 'text-lucky-point-500'])}>{description}</span>
								)}
							</div>
							{/* icon */}
							{isDefined(icon) && <span className="text-lg">{icon}</span>}
						</div>
					}
					disabled={isDisabled}
					styles={(theme) => ({
						root: {
							marginBottom: index === data.length - 1 ? '0px' : '8px',
							paddingLeft: theme.spacing.sm,
							paddingRight: theme.spacing.sm,
							minHeight: '48px',
							display: 'flex',
							flexDirection: 'column',
							borderRadius: theme.radius.lg,
							border: `1px solid ${theme.colors.zinc[2]}`,
							...(isActive && {
								borderColor: theme.colors['purple-heart'][5],
								backgroundColor: theme.colors['purple-heart'][0],
								fontWeight: 'bold',
							}),
							...(isDisabled && {
								borderColor: theme.colors.zinc[2],
								backgroundColor: theme.colors.zinc[0],
							}),
						},
						body: {
							flexGrow: 1,
						},
						labelWrapper: {
							width: '100%',
						},
						inner: {
							alignSelf: 'center',
						},
						label: {
							...(!isDisabled && { cursor: 'pointer' }),
							width: '100%',
							height: '100%',
							display: 'flex',
							color: theme.colors.zinc[5],
							...(isActive && {
								color: theme.colors['purple-heart'][5],
							}),
							...(isDisabled && { cursor: 'not-allowed', opacity: '0.25' }),
						},
						radio: {
							borderColor: theme.colors.zinc[3],
							...(!isDisabled && { cursor: 'pointer' }),
							...(isDisabled && {
								cursor: 'not-allowed',
								borderColor: `${theme.colors.zinc[2]} !important`,
								background: `transparent !important`,
							}),
						},
					})}
				/>
			);

			if (isDefined(renderOption)) {
				return renderOption({ radioNode, data: item });
			}

			return radioNode;
		})}
	</MantineRadio.Group>
);

/**
 * RadioGroup component display name
 * @type {ComponentDisplayName}
 */
RadioGroup.displayName = 'component/RadioGroup';
